/**
 * @file Utilities for working with the Moment library.
 */

// Import supported locale data
import 'moment/locale/fr';

import moment from 'moment-timezone';
import { LanguageCodes } from '../constants';

// Reset the default time zone to local
moment.tz.setDefault();

/**
 * Date formats used in the application. Must use valid Moment tokens.
 * For locale-specific formats, add a custom token to global configuration too.
 * See documentation: https://momentjs.com/docs/#/displaying/format.
 * Special tokens `LL` and `LLL` are language specific and configured in `index.js` file.
 */
export const DateFormats = {
  // Custom tokens for locale-specific dates (must map to global configuration below)
  DEFAULT: 'DEFAULT',
  DEFAULT_WITH_TIME: 'DEFAULT_WITH_TIME',
  MONTH_DAY: 'MONTH_DAY',
  YEAR_MONTH_DAY_TIME: 'YEAR_MONTH_DAY_TIME',
  // Plain Moment tokens
  YEAR: 'YYYY',
  MONTH_YEAR_LONG: 'MMMM YYYY',
  MONTH_YEAR_SHORT: 'MMM YYYY',
  PDF_FILENAME: 'D MMM, YYYY',
  QUERY_PARAM: 'YYYY-MM-DD',
};

// Update global locale configurations with custom tokens for locale-specific formats
moment.updateLocale(LanguageCodes.EN, {
  longDateFormat: {
    [DateFormats.DEFAULT]: 'MMM D, YYYY',
    [DateFormats.DEFAULT_WITH_TIME]: 'MMM D, YYYY, HH:mm z',
    [DateFormats.MONTH_DAY]: 'MMM D',
    [DateFormats.YEAR_MONTH_DAY_TIME]: 'MMM D, YYYY [at] HH:mm',
  },
});
moment.updateLocale(LanguageCodes.FR, {
  longDateFormat: {
    [DateFormats.DEFAULT]: 'D MMM YYYY',
    [DateFormats.DEFAULT_WITH_TIME]: 'D MMM YYYY, HH:mm z',
    [DateFormats.MONTH_DAY]: 'D MMM',
    [DateFormats.YEAR_MONTH_DAY_TIME]: 'D MMM YYYY [à] HH:mm',
  },
});

/**
 * Assume ISO standard.
 * Dates ending with "Z" are parsed in UTC and then converted to the given time zone, if a value is specified.
 * Other dates will be parsed in the given time zone or, if no value is specified, the device time zone.
 * @param {string} value The date to parse.
 * @param {string} timezone The time zone to use. Defaults to device time zone.
 * @returns {moment} The parsed date.
 */
export const parseDate = (value, timezone) => {
  if (timezone) {
    return value.includes('Z')
      ? moment.utc(value).tz(timezone)
      : moment.tz(value, timezone);
  }

  return value.includes('Z') ? moment.utc(value) : moment(value);
};

/**
 * Get the formatted date according to the format and time zone passed in.
 * Assume ISO standard.
 * Dates ending with "Z" are parsed in UTC and then converted to the given time zone, if a value is specified.
 * Other dates will be parsed in the given time zone or, if no value is specified, the device time zone.
 * @param {moment | string} value The date to format.
 * @param {string} format The format to use. Use default without time if none provided.
 * @param {string} timezone The time zone to use. Defaults to device time zone.
 * @returns {string} The formatted date.
 */
export const formatDate = (
  value,
  format = DateFormats.DEFAULT,
  timezone = null,
) => {
  const date = moment.isMoment(value) ? value : parseDate(value, timezone);
  return date.format(moment().localeData().longDateFormat(format) || format);
};
